import { API_BASE_URL, API_ROOT_URL } from './config';
import { getStoredItem, hydrateDeviceStorage, removeStoredItem, setStoredItem } from '../utils/deviceStorage';

export type Member = {
  id: number;
  name: string;
  first_name?: string;
  last_name?: string;
  email?: string | null;
  avatar?: string | null;
};

type ApiEnvelope<T> = {
  data?: T;
  message?: string;
  error?: boolean;
  errors?: Record<string, string[]>;
};

type AuthResponse = {
  token: string;
  token_type: string;
  is_new_user?: boolean;
  member: Member;
  user?: {
    id: number;
    email?: string | null;
    phone?: string | null;
  };
  profiles?: unknown[];
};

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'user';

async function request<T>(path: string, options: RequestInit = {}) {
  const response = await fetch(`${API_ROOT_URL}${path}`, {
    ...options,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(options.headers ?? {}),
    },
  });
  const json = await response.json().catch(() => ({})) as ApiEnvelope<T>;

  if (!response.ok || json.error) {
    throw new Error(firstApiError(json) || json.message || `API ${response.status}: ${path}`);
  }

  return json.data as T;
}

export function getAuthToken() {
  return getStoredItem(TOKEN_KEY);
}

export function getCurrentMember(): Member | null {
  const raw = getStoredItem(USER_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as Member;
  } catch {
    return null;
  }
}

export function isLoggedIn() {
  return Boolean(getAuthToken());
}

export async function bootstrapAuthStorage() {
  await hydrateDeviceStorage();
}

export async function saveSession(data: AuthResponse) {
  await setStoredItem(TOKEN_KEY, data.token);
  await setStoredItem('token', data.token);
  await setStoredItem('access_token', data.token);
  await setStoredItem(USER_KEY, JSON.stringify(data.member));
}

export async function clearSession() {
  await Promise.all([TOKEN_KEY, 'token', 'access_token', USER_KEY, 'profile_id', 'active_child_profile'].map((key) => removeStoredItem(key)));
}

export function authHeaders() {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } as Record<string, string> : {};
}

export async function login(loginValue: string, password: string) {
  const data = await legacyRequest<AuthResponse>('/audiobook-auth/login', {
    method: 'POST',
    body: JSON.stringify({ email: loginValue, password }),
  });
  await saveSession(data);
  return data;
}

export async function parentLogin(loginValue: string, password: string) {
  const data = await request<AuthResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ login: loginValue, password }),
  });
  await saveSession(data);
  return data;
}

export async function register(loginValue: string, password: string, name?: string) {
  const data = await legacyRequest<AuthResponse>('/audiobook-auth/register', {
    method: 'POST',
    body: JSON.stringify({
      email: loginValue,
      name,
      password,
      password_confirmation: password,
    }),
  });
  await saveSession(data);
  return data;
}

export async function parentRegister(loginValue: string, password: string) {
  const data = await request<AuthResponse>('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ login: loginValue, password }),
  });
  await saveSession(data);
  return data;
}

export async function parentFirebaseLogin(idToken: string) {
  const data = await request<AuthResponse>('/auth/firebase', {
    method: 'POST',
    body: JSON.stringify({ id_token: idToken }),
  });
  await saveSession(data);
  return data;
}

export function forgotPassword(email: string) {
  return legacyRequest<unknown>('/audiobook-auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

async function legacyRequest<T>(path: string, options: RequestInit = {}) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(options.headers ?? {}),
    },
  });
  const json = await response.json().catch(() => ({})) as ApiEnvelope<T>;

  if (!response.ok || json.error) {
    throw new Error(firstApiError(json) || json.message || `API ${response.status}: ${path}`);
  }

  return json.data as T;
}

function firstApiError<T>(json: ApiEnvelope<T>) {
  const first = Object.values(json.errors ?? {})[0]?.[0];
  return first || null;
}

export async function logout() {
  const token = getAuthToken();
  if (token) {
    await request<unknown>('/auth/logout', {
      method: 'POST',
      headers: authHeaders(),
    }).catch(() => null);
  }

  await clearSession();
}
